import { useState } from 'react';
import { Download, FileAudio, FileText, Image, Lock, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLang } from '@/lib/i18n';
import { formatFileSize, parseAttachmentPayload } from '@/lib/upload';
import type { AttachmentPayload } from '@/types/attachment';

interface ChatMessageProps {
  message: {
    type: 'message' | 'system' | 'private_message';
    from?: string;
    to?: string;
    content: string;
    timestamp?: string;
    id?: string;
  };
  isMe: boolean;
  myName?: string;
}

function renderTextWithLinks(content: string) {
  const urlRegex = /(https?:\/\/[^\s<>'")]+)(?=[\s.,;:!?)]|$)/gi;
  const parts: Array<string | { type: 'link'; href: string; text: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlRegex.exec(content)) !== null) {
    const url = match[0];
    const start = match.index;

    if (start > lastIndex) {
      parts.push(content.slice(lastIndex, start));
    }

    parts.push({ type: 'link', href: url, text: url });
    lastIndex = start + url.length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.map((part, index) => {
    if (typeof part === 'string') {
      return <span key={`text-${index}`} className="break-words whitespace-pre-wrap">{part}</span>;
    }

    return (
      <a
        key={`link-${index}`}
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-blue-600 underline-offset-2 hover:underline"
      >
        {part.text}
      </a>
    );
  });
}

function getImageFileType(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase() || 'IMAGE';
  return ext;
}

function getVideoFileType(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase() || 'VIDEO';
  return ext;
}

function formatVideoDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
}

function downloadFile(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function renderAttachmentContent(payload: AttachmentPayload, isMobile: boolean, onOpenImage?: (url: string, filename: string) => void) {
  if (payload.type === 'image') {
    // Mobile: simple card with download
    if (isMobile) {
      return (
        <div className="flex flex-col items-center gap-4 py-2">
          <button
            type="button"
            onClick={() => downloadFile(payload.url, payload.filename)}
            className="group flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-700 transition-all duration-200 hover:shadow-lg hover:scale-110 active:scale-95"
            aria-label={`Télécharger ${payload.filename}`}
          >
            <Image className="h-6 w-6 fill-white text-white" />
          </button>
          <div className="text-center">
            <p className="truncate text-sm font-medium text-foreground">{payload.filename}</p>
            <div className="mt-1.5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <span>Image</span>
              <span>•</span>
              <span>{getImageFileType(payload.filename)}</span>
            </div>
          </div>
        </div>
      );
    }

    // Desktop: preview with full viewer
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => onOpenImage?.(payload.url, payload.filename)}
          className="group block w-full overflow-hidden rounded-xl border border-border/40 bg-muted/20 p-1 text-left"
          aria-label={`Agrandir ${payload.filename}`}
        >
          <img
            src={payload.url}
            alt={payload.filename}
            loading="lazy"
            className="max-h-[260px] w-full rounded-lg object-contain sm:max-h-[320px]"
          />
        </button>
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{payload.filename}</p>
          <p>{formatFileSize(payload.size)}</p>
        </div>
      </div>
    );
  }

  if (payload.type === 'audio') {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-border/40 bg-background/60 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileAudio className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{payload.filename}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(payload.size)}</p>
          </div>
          <a
            href={payload.url}
            download={payload.filename}
            className="flex shrink-0 items-center justify-center rounded-full border border-border/40 bg-background/80 p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation"
            aria-label={`Télécharger ${payload.filename}`}
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
        <audio
          controls
          preload="metadata"
          className="w-full"
          src={payload.url}
        >
          Votre navigateur ne peut pas lire ce fichier audio.
        </audio>
      </div>
    );
  }

  if (payload.type === 'video') {
    return (
      <div className="flex flex-col items-center gap-4 py-2">
        <button
          type="button"
          onClick={() => downloadFile(payload.url, payload.filename)}
          className="group flex h-20 w-20 items-center justify-center rounded-full transition-transform duration-200 hover:scale-105 active:scale-95 sm:h-24 sm:w-24"
          aria-label={`Télécharger ${payload.filename}`}
          title={`Télécharger ${payload.filename}`}
        >
          <img
            src="/download-room.png"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-contain"
          />
        </button>
        <div className="text-center">
          <p className="truncate text-sm font-medium text-foreground sm:text-base">{payload.filename}</p>
          <div className="mt-1.5 flex items-center justify-center gap-2 text-xs text-muted-foreground sm:text-sm">
            <span>Vidéo</span>
            <span>•</span>
            <span>{getVideoFileType(payload.filename)}</span>
            <span>•</span>
            <span>{formatVideoDuration(payload.duration ?? 0)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-xl border border-border/40 bg-background/70 p-3 shadow-sm">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/70 text-muted-foreground">
        <FileText className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{payload.filename}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{formatFileSize(payload.size)}</p>
      </div>
      <a
        href={payload.url}
        download={payload.filename}
        className="flex shrink-0 items-center justify-center rounded-full border border-border/40 bg-background/80 p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation"
        aria-label={`Télécharger ${payload.filename}`}
      >
        <Download className="h-4 w-4" />
      </a>
    </div>
  );
}

export function ChatMessage({ message, isMe, myName }: ChatMessageProps) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [imageViewer, setImageViewer] = useState<{ url: string; filename: string } | null>(null);
  const attachmentPayload = parseAttachmentPayload(message.content);

  const renderMessageBody = () => {
    if (attachmentPayload) {
      return renderAttachmentContent(attachmentPayload, isMobile, (url, filename) => setImageViewer({ url, filename }));
    }

    return (
      <div className="break-words whitespace-pre-wrap text-[15px] leading-relaxed">
        {renderTextWithLinks(message.content)}
      </div>
    );
  };

  if (message.type === 'system') {
    return (
      <div className="w-full flex justify-center py-2 animate-in fade-in duration-500">
        <span className="text-xs italic text-muted-foreground bg-muted/30 px-3 py-1 rounded-full border border-border/20 backdrop-blur-sm">
          {message.content}
        </span>
      </div>
    );
  }

  if (message.type === 'private_message') {
    const isSender = message.from === myName;
    const otherPerson = isSender ? message.to : message.from;
    const label = isSender
      ? `${t('pm.you')} → ${otherPerson}`
      : `${otherPerson} (${t('pm.private')})`;

    return (
      <>
        <div className={`flex w-full ${isSender ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300 mb-4`}>
          <div className={`flex flex-col max-w-[88%] sm:max-w-[75%] ${isSender ? 'items-end' : 'items-start'}`}>
            <span className="text-[11px] text-primary/70 mb-1 flex items-center gap-1 font-medium tracking-wide px-1">
              <Lock className="h-2.5 w-2.5" />
              {label}
            </span>
            <div className={`
              px-4 py-3 rounded-2xl shadow-sm
              border border-primary/30 backdrop-blur-md
              ${isSender
                ? 'bg-primary/20 text-foreground rounded-tr-sm'
                : 'bg-primary/10 text-foreground rounded-tl-sm'
              }
            `}>
              {renderMessageBody()}
            </div>
            <div className={`text-[10px] text-muted-foreground mt-1 opacity-70 ${isSender ? 'mr-1' : 'ml-1'}`}>
              {message.timestamp}
            </div>
          </div>
        </div>

        {imageViewer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-6" onClick={() => setImageViewer(null)}>
            <div className="relative max-h-full w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                onClick={() => setImageViewer(null)}
                className="absolute right-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-background/80 text-foreground shadow-lg backdrop-blur"
                aria-label="Fermer l’image"
              >
                <X className="h-5 w-5" />
              </button>
              <img
                src={imageViewer.url}
                alt={imageViewer.filename}
                className="max-h-[85vh] w-full rounded-2xl object-contain shadow-2xl"
              />
              <div className="mt-3 rounded-xl bg-background/90 px-3 py-2 text-sm text-muted-foreground backdrop-blur">
                {imageViewer.filename}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300 mb-4`}>
        <div className={`flex flex-col max-w-[88%] sm:max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
          {!isMe && (
            <span className="text-[11px] text-muted-foreground ml-2 mb-1 font-medium tracking-wide">
              {message.from}
            </span>
          )}
          <div
            className={`
              px-4 py-3 rounded-2xl shadow-sm
              ${isMe
                ? 'bg-[var(--bubble-me)] text-[var(--bubble-me-foreground)] rounded-tr-sm border border-white/5'
                : 'bg-[var(--bubble-other)] text-[var(--bubble-other-foreground)] rounded-tl-sm border border-border/30 backdrop-blur-md'
              }
            `}
          >
            {renderMessageBody()}
          </div>
          <div className={`text-[10px] text-muted-foreground mt-1 opacity-70 ${isMe ? 'mr-1' : 'ml-1'}`}>
            {message.timestamp}
          </div>
        </div>
      </div>

      {imageViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-6" onClick={() => setImageViewer(null)}>
          <div className="relative max-h-full w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={() => setImageViewer(null)}
              className="absolute right-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-background/80 text-foreground shadow-lg backdrop-blur"
              aria-label="Fermer l’image"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={imageViewer.url}
              alt={imageViewer.filename}
              className="max-h-[85vh] w-full rounded-2xl object-contain shadow-2xl"
            />
            <div className="mt-3 rounded-xl bg-background/90 px-3 py-2 text-sm text-muted-foreground backdrop-blur">
              {imageViewer.filename}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
